import { Time, UTCTimestamp } from 'lightweight-charts'
import {
    compareSecurityKey,
    DataUpdateRequestType,
    extractContractSwitchesFromCandles,
    fillCandles,
    getActiveOrNextTradingSession,
    getCurrentCandleStartTime,
    getInitialCandle,
    getTradingSessionsForDate,
    isSpecialChartType,
    mergeCandles,
    prepareSeriesData
} from '../../util/chart'
import { calculateCandlesBetweenDates } from '../../util/chartUtils'
import { convertFromUTC, convertToTimeZone, istToUTC, toDayFormat, toDayStart, utcToIST } from '../../util/dateTime'
import { evaluateHistoricalData } from '../../util/expression'
import { getExpressionSecurityKey, getSecurityKey } from '../../util/security'
import { ChartCandle, ContractSwitch } from './chart.typings'
import { getCandlesBulkV2 } from '../../services/apis/candles'
import Chart from './chart'
import { WebSocketResponseMessage } from '../../proto/generated/websocket'
import {
    CANDLE_THRESHOLD,
    EXTRA_CANDLES_COUNT
} from '../../assets/constants'
import { getCandleRequestV2, getTimeWidthInSeconds } from './chartHelpers'
import { CorporateAction } from '../../proto/generated/corporate-action'
import { GetCandlesBulkResponseV2, GetCandlesRequestV2, GetCandlesResponseV2 } from '../../proto/generated/tick-data'
import { Security } from '../../proto/generated/security'
import { Candle } from '../../proto/generated/candle-data-depot-common'
import { CompanyEventProto } from '../../proto/generated/company_event'
import { TradingCalendarData } from '../../proto/generated/trading-calendar'
import { isIntraday } from '../../util/timeBuckets'
import type { OrdersOnChartToolState } from './drawingTools/ordersOnChartTool/OrdersOnChartTool'
import type { OrdersMarkersDrawingState } from './drawingTools/ordersOnChartTool/OrderMarkers'
import { ChartAlertsOnChartState } from './drawingTools/chartAlertsOnChartTool/ChartAlertsOnChartTool'
import { NewsArticle } from '../../proto/generated/market_news'

/** Candles per backfill request, and requests per security, when re-fetching bars missed while live. */
const BACKFILL_CHUNK = 500
const BACKFILL_MAX_ROUNDS = 4
const BACKFILL_MAX_RETRIES = 3
/** Returning to a tab hidden at least this long, with a socket quiet at least this long, triggers a backfill. */
const BACKFILL_MIN_HIDDEN_MS = 60_000
const BACKFILL_QUIET_SOCKET_MS = 20_000

export type ChartDataManagerOptions = {
    chart: Chart
}

class ChartDataManager {
    chart: Chart
    data: Record<string, ChartCandle[]> = {}
    replayData: Record<string, ChartCandle[]> = {}
    advanceData: Record<string, Candle[]> = {}
    prevOutOfData: Record<string, boolean> = {}
    nextOutOfData: Record<string, boolean> = {}
    previousTokens: Record<string, string> = {}
    dataLoading = false
    /** Bumped per fetch; a response whose generation is stale (superseded by a newer initial load) is discarded. */
    private fetchGeneration = 0
    fillEmptySlots = true
    corporateActions: CorporateAction[] = []
    corporateActionsByDate: Record<number, CorporateAction[]> = {};
    companyEventsByDate: Record<number, CompanyEventProto[]> = {};
    companyEvents: CompanyEventProto[] = []
    ordersOnChart: OrdersOnChartToolState = {
        positions: [],
        openOrders: [],
        ltp: 0,
        topAsk: 0,
        topBid: 0
    }
    ordersMarkersOnChart: OrdersMarkersDrawingState = {
        orders: []
    }
    chartAlertsOnChart: ChartAlertsOnChartState = {
        alerts: []
    }
    futureCompanyEvents: CompanyEventProto[] = []
    newsArticlesByDate: Record<number, NewsArticle[]> = {}
    newsArticles: NewsArticle[] = []
    contractSwitches: ContractSwitch[] = []
    referenceSecurities: Record<string, Security> = {}
    forwardDataPending = false;
    updateSocketData: boolean = false
    socketUpdatedBeforeReplay = false;
    calendarData: TradingCalendarData | undefined
    dataRequestType?: DataUpdateRequestType
    candleCheckTimeout?: number
    private realtimeListenerAttached = false
    /** Wall-clock ms of the last socket message of any kind: a quiet socket means ticks may have been missed. */
    private lastSocketMessageAt = 0
    private hiddenAt = 0
    private backfillTimer?: number
    private pendingBackfillFrom?: number
    private backfillFailures = 0

    constructor(options: ChartDataManagerOptions) {
        this.chart = options.chart
        if (!this.chart.disableRealtimeUpdates) {
            this.enableRealtimeUpdates()
        }
        this.calendarData = this.chart.tradingCalendarData
    }

    /** Attach WS candle listener and subscribe when realtime was off at construction (e.g. prop removed later). */
    enableRealtimeUpdates() {
        if (this.realtimeListenerAttached) return
        this.realtimeListenerAttached = true
        this.chart.disableRealtimeUpdates = false
        this.chart.ws.addListener(this.onWebSocketMessageReceived)
        this.chart.ws.addOpenListener(this.onSocketOpen)
        document.addEventListener('visibilitychange', this.onVisibilityChange)
        document.addEventListener('resume', this.onPageResume)
        this.updateSocketData = true
        const securities = this.chart.securities
        if (securities.length) {
            this.chart.subscribeSecurities(securities.concat(this.chart.comparableSecurities))
        }
    }

    private updateTradingCalendarInfo = () => {
        if (this.calendarData !== this.chart.tradingCalendarData) {
            this.calendarData = this.chart.tradingCalendarData
        }
    }

    fetchData = async ({ initial = false, updateConfig = false, fetchCompulsory = false }): Promise<boolean> => {
        this.updateTradingCalendarInfo()
        // An initial (re)load supersedes an in-flight one: dropping it left chart.timeBucket / chart.dateRange
        // already switched while the stale response rendered, or (after an epoch bump) dataLoading stuck true.
        if (this.dataLoading && !initial) {
            return false
        }
        const fetchGeneration = ++this.fetchGeneration
        // checkForCandleComplete can set LIVE on the shared field while this request is in flight.
        const requestType = initial ? DataUpdateRequestType.INITIAL : DataUpdateRequestType.HISTORICAL

        const securities = this.chart.securities
        const backCandlesRequired: Record<string, number> = {}
        const forwardCandlesRequired: Record<string, number> = {}
        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket);
        let candleMultiplier = isSpecialChartType(this.chart.chartConfig.chartType) ? 4 : 1;
        if (!this.chart.disableRealtimeUpdates) {
            this.chart.subscribeSecurities(securities.concat(this.chart.comparableSecurities))
        }
        if (initial) {
            this.data = {}
            this.replayData = {}
            this.prevOutOfData = {}
            this.nextOutOfData = {}
            this.previousTokens = {}
            this.advanceData = {}
            this.corporateActions = []
            this.corporateActionsByDate = {}
            this.companyEvents = [];
            this.futureCompanyEvents = [];
            this.companyEventsByDate = {};
            this.newsArticles = []
            this.newsArticlesByDate = {}
            this.contractSwitches = []
            this.referenceSecurities = {}
            this.updateSocketData = false
            this.forwardDataPending = false;
        }
        this.dataRequestType = requestType
        let { rangeStart, barsAfter } = this.chart.viewportCandleInfo
        const viewportCandles = this.chart.viewportCandleInfo.viewportCandles;
        let dateRangeCandleCount = 0
        const selectedDateRange = this.chart.dateRange

        let updateSocketData = false
        if (selectedDateRange) {
            if (initial) {
                const dateRange: [Date, Date] = [new Date(selectedDateRange.startTime), new Date(selectedDateRange.endTime || Date.now())]
                dateRangeCandleCount = Math.ceil(
                    calculateCandlesBetweenDates(dateRange, {
                        timeSeries: this.chart.timeBucket.timeSeries,
                        value: this.chart.timeBucket.timeValue
                    }))
            } else {
                const needsCompareBootstrap = this.chart.comparableSecurities.some((sec) => {
                    const key = getSecurityKey(sec.exchange, sec.code)
                    return !(this.data[key]?.length)
                })
                if (!needsCompareBootstrap) {
                    this.setDataLoading(false)
                    return false
                }
            }

        }

        const gotoDateCandleCount = Math.ceil(viewportCandles / 2)
        const chartBackwardCandlesNeeded = this.chart.gotoDate ? gotoDateCandleCount : (this.chart.getRawData().length - (initial ? EXTRA_CANDLES_COUNT : 0) - rangeStart + viewportCandles + CANDLE_THRESHOLD)
        const chartForwardCandlesNeeded = this.chart.gotoDate && initial ? gotoDateCandleCount : viewportCandles + CANDLE_THRESHOLD - barsAfter

        const backwardCandleRequests: GetCandlesRequestV2[] = []
        const forwardCandleRequests: GetCandlesRequestV2[] = []

        const extraCandleCount = selectedDateRange ? 0 : (initial && !this.chart.gotoDate ? EXTRA_CANDLES_COUNT * 2 : (rangeStart >= 0 ? Math.max(viewportCandles, EXTRA_CANDLES_COUNT) : Math.abs(rangeStart) + EXTRA_CANDLES_COUNT))
        const backwardRequestDedupe = new Set<string>()
        const pushBackwardRequest = (req: GetCandlesRequestV2) => {
            if (req.backwardCandleCount <= 0) return
            const sk = getSecurityKey(req.security.exchange, req.security.code)
            const dedupeKey = `${sk}|${req.referenceTime}|${req.backwardCandleCount}|${req.forwardCandleCount}`
            if (backwardRequestDedupe.has(dedupeKey)) return
            backwardRequestDedupe.add(dedupeKey)
            backwardCandleRequests.push(req)
        }
        securities.forEach((sec) => {
            const key = getSecurityKey(sec.exchange, sec.code)
            const existingData = this.data[key] || []
            const backwardFetchCount = selectedDateRange ? dateRangeCandleCount : chartBackwardCandlesNeeded - existingData.length
            backCandlesRequired[key] = Math.max(0, Math.min(backwardFetchCount + extraCandleCount, 500) * candleMultiplier)
            if (initial) {
                backwardCandleRequests.push(getCandleRequestV2(
                    backCandlesRequired[key],
                    this.chart.gotoDate ? chartForwardCandlesNeeded + extraCandleCount : 0,
                    sec,
                    this.chart.timeBucket,
                    selectedDateRange ? selectedDateRange.endTime || Date.now() : this.chart.gotoDate ? convertFromUTC(this.chart.gotoDate, this.chart.timezone) * 1000 : 0
                ))
            } else {
                if ((fetchCompulsory || backwardFetchCount > 0) && !this.prevOutOfData[key] && backCandlesRequired[key] > 0) {
                    let referenceTime = Math.max(0, istToUTC((existingData[0]?.time as number) || 0) * 1000)
                    pushBackwardRequest(
                        getCandleRequestV2(
                            backCandlesRequired[key],
                            0,
                            sec,
                            this.chart.timeBucket,
                            referenceTime
                        )
                    )
                }

                if ((fetchCompulsory || chartForwardCandlesNeeded > 0) && !this.updateSocketData && !this.nextOutOfData[key]) {
                    const [lastCandle] = existingData.slice(-1)
                    const lastCandleTime = istToUTC((lastCandle?.time as number) || 0) * 1000
                    let referenceTime = Math.max(0, lastCandleTime)
                    forwardCandlesRequired[key] = Math.min(chartForwardCandlesNeeded, 500) * candleMultiplier;
                    forwardCandleRequests.push(getCandleRequestV2(
                        0,
                        chartForwardCandlesNeeded + extraCandleCount,
                        sec,
                        this.chart.timeBucket,
                        referenceTime,
                        false,
                        true
                    ))
                }
            }
        })

        this.chart.comparableSecurities.forEach(sec => {
            const key = getSecurityKey(sec.exchange, sec.code)
            const existingData = this.data[key] || []
            const backwardFetchCount = selectedDateRange ? dateRangeCandleCount : chartBackwardCandlesNeeded - existingData.length
            const primaryData = this.chart.getRawData()
            const [lastCandle] = primaryData.slice(-1)
            backCandlesRequired[key] = Math.max(0, Math.min(backwardFetchCount + extraCandleCount, 500) * candleMultiplier)
            if (existingData.length === 0) {
                pushBackwardRequest(
                    getCandleRequestV2(
                        Math.max(1, backCandlesRequired[key] || Math.min(chartBackwardCandlesNeeded + extraCandleCount, 500) * candleMultiplier),
                        initial && this.chart.gotoDate ? chartForwardCandlesNeeded : 0,
                        sec,
                        this.chart.timeBucket,
                        initial && this.chart.gotoDate ? convertFromUTC(this.chart.gotoDate, this.chart.timezone) * 1000 : lastCandle ? convertFromUTC(lastCandle.time as number, this.chart.timezone) * 1000 + 100 : 0
                    )
                )
            } else {
                if ((fetchCompulsory || backwardFetchCount > 0) && !this.prevOutOfData[key] && backCandlesRequired[key] > 0) {
                    let referenceTime = Math.max(0, (convertFromUTC(existingData[0]?.time as number, this.chart.timezone) || 0) * 1000)
                    pushBackwardRequest(
                        getCandleRequestV2(
                            backCandlesRequired[key],
                            0,
                            sec,
                            this.chart.timeBucket,
                            referenceTime
                        )
                    )
                }

                if ((fetchCompulsory || chartForwardCandlesNeeded > 0) && !this.nextOutOfData[key]) {
                    const [lastCandle] = existingData.slice(-1)
                    let referenceTime = Math.max(0, ((lastCandle?.time as number) || 0) * 1000)
                    if (toDayFormat(referenceTime) !== toDayFormat(Date.now())) {
                        forwardCandlesRequired[key] = Math.min(chartForwardCandlesNeeded, 500) * candleMultiplier;
                        forwardCandleRequests.push(getCandleRequestV2(
                            0,
                            chartForwardCandlesNeeded,
                            sec,
                            this.chart.timeBucket,
                            referenceTime
                        ))
                    }
                }
            }
        })


        const promises: Promise<GetCandlesBulkResponseV2 | null>[] = []
        let requestCount = 0

        if (backwardCandleRequests.length) {
            requestCount++
            promises.push(getCandlesBulkV2(
                backwardCandleRequests
            ))
        } else {
            promises.push(Promise.resolve(null))
        }

        if (!forwardCandleRequests.length || initial) {
            promises.push(Promise.resolve(null))
        } else {
            requestCount++
            promises.push(getCandlesBulkV2(
                forwardCandleRequests
            ))
        }

        if (!requestCount) {
            this.setDataLoading(false)
            this.chart.onDataLoaded(false, updateConfig, requestType)
            return false
        } else {
            this.setDataLoading(true)
        }

        let responses: (GetCandlesBulkResponseV2 | null)[]
        try {
            responses = await Promise.all(promises)
        } catch (error) {
            console.error('chart: candle fetch failed', error)
            // Left true, dataLoading parks every later WS tick in advanceData and refuses all further fetches.
            if (!this.chart.destroyed && fetchGeneration === this.fetchGeneration) {
                this.setDataLoading(false)
                initial && this.chart.callbacks.onLoading?.(false)
            }
            return false
        }
        if (this.chart.destroyed) return false
        // Superseded while in flight: the newer load owns dataLoading and will render; don't apply stale candles.
        if (fetchGeneration !== this.fetchGeneration) return false
        this.dataRequestType = requestType

        const backwardBulkCandleResponse = responses[0] as GetCandlesBulkResponseV2
        const forwardBulkCandleResponse = responses[1] as GetCandlesBulkResponseV2


        if (backwardCandleRequests.length && backwardBulkCandleResponse) {
            backwardBulkCandleResponse.responses.forEach((response, i) => {
                const request = backwardCandleRequests[i];
                if (!response.security) return
                response.referencedSecurities.forEach((v) => {
                    this.referenceSecurities[v.code] = v
                })
                const key = getSecurityKey(response.security.exchange, response.security.code)
                if (this.chart.mode !== 'EXPRESSION' && compareSecurityKey(this.chart.securities[0], key)) {
                    this.updateCorporateActions(response.corporateActions)
                    this.updateCompanyEvents(response.companyEvents)
                    this.updateNewsArticles(response.newsArticles)
                }
                const security = this.chart.securityMap[key]
                if (!security) return
                const existingData = this.data[key] || []
                if (response.backwardCandles.length === 0) {
                    this.prevOutOfData[key] = true
                }
                const fetchedData = prepareSeriesData({ candles: response.backwardCandles, priceMultiplier: security.priceMultiplier || 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security })
                if (fetchedData.length && existingData.length) {
                    fetchedData.push(...fillCandles({ startCandle: fetchedData[fetchedData.length - 1], endCandle: existingData[0], timeBucket: this.chart.timeBucket, calendarData: this.chart.calendarData, timezone: this.chart.timezone }))
                }
                this.data[key] = [...fetchedData, ...existingData]
                if (response.forwardCandles && response.forwardCandles.length) {
                    const updatedData = [...this.data[key]]
                    const lastCandle = updatedData[updatedData.length - 1]
                    const lastCandleTime = lastCandle?.time ?? 0;

                    const forwardCandles = prepareSeriesData({ candles: response.forwardCandles, priceMultiplier: security.priceMultiplier || 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security })
                    if (lastCandle) {
                        updatedData.push(...fillCandles({ startCandle: lastCandle, endCandle: forwardCandles[0], timeBucket: this.chart.timeBucket, calendarData: this.chart.calendarData, timezone: this.chart.timezone }))
                    }
                    if (response.forwardCandles.length < request.forwardCandleCount) {
                        this.nextOutOfData[key] = true
                    }
                    forwardCandles.forEach((c) => {
                        if (c.time > lastCandleTime) {
                            updatedData.push(c)
                        }
                    })
                    this.data[key] = updatedData
                }
            })
        }

        let reachedForwardEnd = 0
        if (forwardCandleRequests.length && forwardBulkCandleResponse) {
            forwardBulkCandleResponse.responses.forEach((response, i) => {
                if (!response.security) return
                const request = forwardCandleRequests[i]
                response.referencedSecurities.forEach((v) => {
                    this.referenceSecurities[v.code] = v
                })
                const key = getSecurityKey(response.security.exchange, response.security.code)
                if (this.chart.mode !== 'EXPRESSION' && compareSecurityKey(this.chart.securities[0], key)) {
                    this.updateCorporateActions(response.corporateActions)
                    this.updateCompanyEvents(response.companyEvents)
                    this.updateNewsArticles(response.newsArticles)
                }
                const security = this.chart.securityMap[key]
                if (!security) return
                const existingData = this.data[key] || []
                const responseForwardCandles = response.forwardCandles;

                const forwardCandles = prepareSeriesData({ candles: responseForwardCandles, priceMultiplier: security.priceMultiplier ?? 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security })
                const lastCandleTime = existingData[existingData.length - 1]?.time ?? 0
                if (existingData.length && forwardCandles.length) {
                    existingData.push(...fillCandles({ startCandle: existingData[existingData.length - 1], endCandle: forwardCandles[0], timeBucket: this.chart.timeBucket, calendarData: this.chart.calendarData, timezone: this.chart.timezone }))
                }
                forwardCandles.forEach((c) => {
                    if (c.time > lastCandleTime) {
                        existingData.push(c)
                    }
                })
                this.data[key] = existingData

                if (responseForwardCandles.length < request.forwardCandleCount) {
                    reachedForwardEnd++
                    this.nextOutOfData[key] = true
                }
            })

            if (reachedForwardEnd === forwardCandleRequests.length) {
                if (!this.chart.replayMode) {
                    updateSocketData = true;
                }
                this.forwardDataPending = false;
            } else {
                this.forwardDataPending = true;
            }
        }
        if (!this.chart.gotoDate && !this.forwardDataPending && ((backwardCandleRequests.length > 0 && forwardCandleRequests.length === 0) || (initial && !this.chart.disableRealtimeUpdates))) {
            if (!this.chart.replayMode) {
                updateSocketData = true;
            }
        }

        if (updateSocketData) {
            this.mergeAdvanceData();
        }
        if (this.chart.destroyed) return false
        this.chart.chartApi.applyOptions({
            timeScale: {
                shiftVisibleRangeOnNewBar: true,
                allowShiftVisibleRangeOnWhitespaceReplacement: true,
                borderVisible: false
            }
        })
        this.createPrimaryData(backwardCandleRequests.length > 0 || forwardCandleRequests.length > 0);
        this.updateSocketData = updateSocketData
        if (this.updateSocketData) {
            const primaryData = this.chart.getRawData();

            if (primaryData.length) {
                const lastCandle = primaryData[primaryData.length - 1];
                const candleEndTime = (lastCandle.time as number) + 2 * timeWidth;
                window.clearTimeout(this.candleCheckTimeout)
                this.candleCheckTimeout = window.setTimeout(this.checkForCandleComplete, istToUTC(candleEndTime) * 1000 - Date.now() + 1200);
            }
        }

        this.chart.onDataLoaded(true, updateConfig, requestType)
        return true
    }

    mergeAdvanceData() {
        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        if (!this.chart.tradingCalendarData) return;
        for (const key in this.advanceData) {
            const secKey = key.replace(this.chart.subscriptionTag, '')
            const sec = this.chart.securityMap[secKey]
            // continue, not break: one stale key (e.g. the previous strike) must not drop every other security's ticks
            if (!sec) continue
            const advanceData = prepareSeriesData({ candles: this.advanceData[key], priceMultiplier: sec.priceMultiplier || 100, timezone: this.chart.timezone, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.chart.tradingCalendarData, security: sec })
            const candleData = this.data[secKey]
            if (!candleData) continue
            let [lastCandle] = candleData.slice(-1)
            if (!lastCandle) continue
            let startIndex = 0
            while (startIndex < advanceData.length && lastCandle.time >= advanceData[startIndex]?.time) {
                startIndex++
            }
            for (let i = startIndex; i < advanceData.length; i++) {
                lastCandle = candleData[candleData.length - 1];
                const updatedCandle = advanceData[i];
                const updatedCandleStartTime = utcToIST(getCurrentCandleStartTime(istToUTC(updatedCandle.time as number), this.chart.chartConfig.timeBucket, this.chart.calendarData!))
                if (updatedCandleStartTime > (lastCandle.time as number) + timeWidth) {
                    candleData.push(...fillCandles({
                        startCandle: lastCandle,
                        endCandle: {
                            ...updatedCandle, time: updatedCandleStartTime as Time
                        }, timeBucket: this.chart.timeBucket, calendarData: this.chart.calendarData, timezone: this.chart.timezone
                    }))
                }
                if (!updatedCandle) break
                lastCandle = candleData[candleData.length - 1]
                const updatedCandleTime = updatedCandle.time as UTCTimestamp
                const previousTime = lastCandle.time as UTCTimestamp
                const previousLastTickTime = lastCandle.lastTickTimestamp as UTCTimestamp
                if (updatedCandleStartTime > previousTime) {
                    candleData.push({
                        ...updatedCandle,
                        time: updatedCandleStartTime as Time
                    })
                    lastCandle = candleData[candleData.length - 1]
                } else if (updatedCandleTime > previousLastTickTime && updatedCandleStartTime === updatedCandleStartTime) {
                    candleData[candleData.length - 1] = {
                        ...lastCandle,
                        close: updatedCandle.close,
                        low: Math.min(updatedCandle.low, lastCandle.low),
                        high: Math.max(updatedCandle.high, lastCandle.high),
                        lastTickTimestamp: updatedCandle.time as number,
                        volume: (lastCandle.volume || 0) + updatedCandle.volume
                    }
                }
            }
            this.data[secKey] = candleData
            this.advanceData[key] = []
        }
    }

    initializeReplayMode = async () => {
        if (!this.chart.replayStartTime) return;
        this.socketUpdatedBeforeReplay = this.updateSocketData
        this.updateSocketData = false;
        const replayStartTime = istToUTC(isIntraday(this.chart.timeBucket.timeSeries) ? this.chart.replayStartTime - 0.1 : this.chart.replayStartTime)
        const replayCandleStartTime = getCurrentCandleStartTime(replayStartTime, this.chart.timeBucket, this.chart.calendarData!)
        const replayCandleStartTimeUTC = utcToIST(replayCandleStartTime)
        for (let key in this.data) {
            const data = this.data[key];
            let index = -1;
            for (let c = data.length - 1; c >= 0; c--) {
                if ((data[c].time as number) === replayCandleStartTimeUTC) {
                    index = c;
                    break;
                }
            }
            if (index >= 0) {
                this.data[key] = data.slice(0, index + 1);
            }
        }
        this.dataRequestType = DataUpdateRequestType.INITIAL
        this.createPrimaryData(true)
        this.chart.onDataLoaded(true, false)
    }

    disableReplayMode = async () => {
        this.dataRequestType = DataUpdateRequestType.INITIAL
        this.mergeAdvanceData();
        this.createPrimaryData(true)
        this.chart.onDataLoaded(true, false)
        this.updateSocketData = this.socketUpdatedBeforeReplay;
        this.socketUpdatedBeforeReplay = false;
    }

    setDataLoading(loading: boolean) {
        this.dataLoading = loading
    }

    updateContractSwitches(contracts: ContractSwitch[]) {
        this.contractSwitches = [...contracts]
    }

    updateCorporateActions(actions: CorporateAction[]) {
        const allActions = [...this.corporateActions, ...actions];
        const uniqueMap = new Map<string, CorporateAction>();
        allActions.forEach(action => {
            const key = `${action.type}-${action.exDate}-${action.recordDate}`;
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, action);
            }
        });
        this.corporateActions = Array.from(uniqueMap.values()).sort(
            (a, b) => a.recordDate - b.recordDate
        );

        this.corporateActionsByDate = this.corporateActions.reduce<Record<number, CorporateAction[]>>((acc, action) => {
            if (!action.exDate || action.exDate === 0) return acc;

            const timeInSeconds = Math.floor(action.exDate / 1000);
            if (!acc[timeInSeconds]) {
                acc[timeInSeconds] = [];
            }
            acc[timeInSeconds].push(action);
            return acc;
        }, {})
    }

    updateCompanyEvents(events: CompanyEventProto[]) {
        this.companyEvents = [...this.companyEvents, ...events].sort((a, b) => Number(a.date) - Number(b.date))
        this.companyEventsByDate = this.groupCompanyEventsByDate();
    }

    updateOrdersOnChart(data: Partial<OrdersOnChartToolState>) {
        if (data.positions) this.ordersOnChart.positions = data.positions
        if (data.openOrders) this.ordersOnChart.openOrders = data.openOrders
    }

    updateOrderMarkersOnChart(data: Partial<OrdersMarkersDrawingState>) {
        if (data.orders) this.ordersMarkersOnChart.orders = data.orders
    }

    updateChartAlertsOnChart(data: Partial<ChartAlertsOnChartState>) {
        if (data.alerts) this.chartAlertsOnChart.alerts = data.alerts
    }

    updateFutureCompanyEvents(events: CompanyEventProto[]) {
        this.futureCompanyEvents = events;
        this.companyEventsByDate = this.groupCompanyEventsByDate();
    }
    groupCompanyEventsByDate(): Record<number, CompanyEventProto[]> {
        const seen = new Set<string>();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return this.companyEvents
            .concat(this.futureCompanyEvents)
            .filter(event => {
                const eventDate = new Date(event.date);
                eventDate.setHours(0, 0, 0, 0);
                return eventDate >= today;
            })
            .reduce<Record<number, CompanyEventProto[]>>((acc, event) => {
                const eventDate = new Date(event.date);
                eventDate.setHours(0, 0, 0, 0);
                const timeKey = eventDate.getTime();
                const key = `${event.primaryCode}-${timeKey}-${event.details?.trim() ?? ''}`;
                if (seen.has(key)) return acc;
                seen.add(key);
                if (!acc[timeKey]) acc[timeKey] = [];
                acc[timeKey].push(event);
                return acc;
            }, {});
    }

    updateNewsArticles(articles: NewsArticle[]) {
        this.newsArticles = [...this.newsArticles, ...articles].sort((a, b) => a.created - b.created)
        this.newsArticlesByDate = this.groupNewsArticlesByDate()
    }

    groupNewsArticlesByDate(): Record<number, NewsArticle[]> {
        const seen = new Set<string>()
        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)

        const candleTimeToArticles: Record<number, NewsArticle[]> = {}
        const candleTimeToTotalCount: Record<number, number> = {}

        this.newsArticles.forEach((article) => {
            const key = `${article.title}-${article.created}-${article.link}`
            if (seen.has(key)) return
            seen.add(key)

            const articleTimeIST = utcToIST(article.created)
            let targetCandleTime: number

            // Use deterministic calculation based on article time and calendar data
            // This ensures consistent results regardless of current chart data
            if (this.chart.calendarData) {
                const candleStartTime = getCurrentCandleStartTime(istToUTC(articleTimeIST), this.chart.chartConfig.timeBucket, this.chart.calendarData)
                targetCandleTime = utcToIST(candleStartTime)
            } else {
                // Fallback to simple time bucket alignment
                targetCandleTime = Math.floor(articleTimeIST / timeWidth) * timeWidth
            }

            if (!candleTimeToArticles[targetCandleTime]) {
                candleTimeToArticles[targetCandleTime] = []
                candleTimeToTotalCount[targetCandleTime] = 0
            }

            candleTimeToTotalCount[targetCandleTime]++

            if (candleTimeToArticles[targetCandleTime].length < 3) {
                candleTimeToArticles[targetCandleTime].push(article)
            } else {
                const oldestIndex = candleTimeToArticles[targetCandleTime].reduce(
                    (oldestIdx, currentArticle, currentIdx, arr) => (currentArticle.created < arr[oldestIdx].created ? currentIdx : oldestIdx),
                    0
                )

                if (article.created > candleTimeToArticles[targetCandleTime][oldestIndex].created) {
                    candleTimeToArticles[targetCandleTime][oldestIndex] = article
                }
            }

            candleTimeToArticles[targetCandleTime].sort((a, b) => b.created - a.created)
        })

        const result: Record<number, NewsArticle[]> = {}
        Object.keys(candleTimeToArticles).forEach((timeKey) => {
            const timeInMs = parseInt(timeKey) * 1000
            const articles = candleTimeToArticles[parseInt(timeKey)]
            result[timeInMs] = articles.map((article) => ({
                ...article,
            }))
        })

        return result
    }

    createPrimaryData = (newDataFetched: boolean) => {
        const requestType = this.dataRequestType
        const securities = this.chart.securities
        if (newDataFetched && securities.length) {
            if (this.chart.mode === 'EXPRESSION') {
                if (!this.chart.expression) return
                for (let i = 0; i < securities.length; i++) {
                    const sec = securities[i]
                    const key = getSecurityKey(sec.exchange, sec.code)
                    if (this.prevOutOfData[key] === true) {
                        this.prevOutOfData['primary'] = true
                        break
                    }
                }
                const data: Record<string, ChartCandle[]> = {}
                let minLength = Math.min(...this.chart.securities.map((sec) => {
                    const key = getSecurityKey(sec.exchange, sec.code)
                    return (this.data[key] || []).length
                }))
                if (minLength === 0) {
                    this.data['primary'] = []
                    return
                }

                if (requestType === DataUpdateRequestType.LIVE) {
                    minLength = 1
                }
                securities.forEach((sec) => {
                    const key = getSecurityKey(sec.exchange, sec.code)
                    data[getExpressionSecurityKey(sec.exchange, sec.code)] = requestType === DataUpdateRequestType.LIVE ? this.data[key].slice(-1) : this.data[key]
                })
                const evalData = evaluateHistoricalData(this.chart.evaluationExpression, data, this.chart.evaluationContext, this.referenceSecurities)
                if (requestType !== DataUpdateRequestType.LIVE) {
                    this.data['primary'] = evalData
                } else {
                    const dataLength = this.data['primary'].length
                    const lastCandle = this.data['primary'][dataLength - 1]
                    const currentCandle = evalData[0]
                    const previousTime = lastCandle.time as UTCTimestamp
                    if (previousTime < (currentCandle.time as UTCTimestamp)) {
                        this.data['primary'].push(currentCandle)
                    } else if (previousTime === (currentCandle.time as UTCTimestamp)) {
                        this.data['primary'][dataLength - 1] = currentCandle
                    }
                }
            } else {
                const key = getSecurityKey(securities[0].exchange, securities[0].code)
                this.data['primary'] = this.data[key]

                if (requestType !== DataUpdateRequestType.LIVE) {
                    const cSwitches = extractContractSwitchesFromCandles(this.data['primary'], this.referenceSecurities)
                    this.updateContractSwitches(cSwitches)
                }

                this.prevOutOfData['primary'] = this.prevOutOfData[key] || false

            }
        }
        const dataLength = this.data['primary']?.length
        if (dataLength > 0) {
            const lastCandle = this.data['primary'][dataLength - 1]
            const priceMultiplier = securities[0]?.priceMultiplier ?? 100

            this.ordersOnChart.ltp = lastCandle?.close ?? 0
            this.ordersOnChart.topBid = lastCandle?.topBid ? (lastCandle.topBid / priceMultiplier) : 0
            this.ordersOnChart.topAsk = lastCandle?.topAsk ? (lastCandle.topAsk / priceMultiplier) : 0
        }
    }

    onWebSocketMessageReceived = (_data: Uint8Array, message: WebSocketResponseMessage) => {
        if (this.chart.destroyed || this.chart.disableRealtimeUpdates || !this.calendarData) return
        const socketQuietMs = this.lastSocketMessageAt ? Date.now() - this.lastSocketMessageAt : 0
        this.lastSocketMessageAt = Date.now()
        const { candleSubscriptionResponses } = message
        const timeWidth: number = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        let publishData = false
        let backfillFrom = Infinity
        if (candleSubscriptionResponses && candleSubscriptionResponses.length > 0) {
            candleSubscriptionResponses.forEach((response) => {
                const { securityCandles, tag } = response
                if (tag === this.chart.subscriptionTag) {
                    securityCandles.forEach(({ candle, security }) => {
                        const key = getSecurityKey(security!.exchange, security!.code)
                        if (this.dataLoading || !this.updateSocketData) {
                            const advanceData = this.advanceData[key + tag] ?? []
                            if (candle && advanceData.findIndex(d => d.startTimestamp === candle.startTimestamp) === -1) {
                                advanceData.push(candle)
                            }
                            this.advanceData[key + tag] = [...advanceData]
                            return
                        } else {
                            publishData = true
                            const candleData = this.data[key]
                            if (!candleData) return
                            const [lastCandle] = candleData.slice(-1)
                            const sec = this.chart.securityMap[key]
                            if (!lastCandle || !candle || !sec) return

                            const [updatedCandle] = prepareSeriesData({ candles: [candle], priceMultiplier: sec.priceMultiplier || 100, timezone: this.chart.timezone, timeBucket: this.chart.timeBucket, fillEmptySlots: true, calendarData: this.calendarData, security: sec })
                            const previousTime = lastCandle.time as UTCTimestamp
                            const previousLastTickTime = lastCandle.lastTickTimestamp as UTCTimestamp
                            const updatedCandleTime = updatedCandle.time as UTCTimestamp
                            const newTime = (previousTime + (Math.floor((updatedCandleTime - previousTime) / timeWidth) * timeWidth))
                            if (updatedCandleTime >= previousTime + timeWidth) {
                                const newCandle: ChartCandle = {
                                    ...updatedCandle,
                                    time: newTime as Time,
                                    lastTickTimestamp: updatedCandle.time as number
                                }
                                // Session slots skipped since the last bar. With the socket alive they had no trades
                                // (illiquid, or checkForCandleComplete ran late), so flat bars are right. If the socket
                                // itself went quiet, ticks were lost (connection down, tab frozen, machine asleep).
                                const skipped = fillCandles({ startCandle: lastCandle, endCandle: newCandle, timeBucket: this.chart.timeBucket, calendarData: this.chart.calendarData, timezone: this.chart.timezone })
                                if (skipped.length && socketQuietMs >= BACKFILL_QUIET_SOCKET_MS) {
                                    backfillFrom = Math.min(backfillFrom, previousTime - timeWidth)
                                }
                                candleData.push(...skipped, newCandle)
                            } else if (updatedCandleTime > previousLastTickTime && updatedCandleTime < previousTime + timeWidth) {
                                candleData[candleData.length - 1] = mergeCandles(lastCandle, updatedCandle)
                            }
                            this.advanceData[key + tag] = []
                        }
                    })
                }
            })
            if (publishData) {
                if (this.chart.destroyed) return
                const timeScaleOptions = this.chart.chartApi.options().timeScale
                if (!timeScaleOptions.shiftVisibleRangeOnNewBar || !timeScaleOptions.allowShiftVisibleRangeOnWhitespaceReplacement) {
                    this.chart.chartApi.applyOptions({
                        timeScale: {
                            shiftVisibleRangeOnNewBar: true,
                            allowShiftVisibleRangeOnWhitespaceReplacement: true
                        }
                    })
                }
                this.dataRequestType = DataUpdateRequestType.LIVE
                this.createPrimaryData(true)
                this.chart.scheduleLiveDataLoaded()
            }
            if (Number.isFinite(backfillFrom)) this.scheduleBackfill(backfillFrom)
        }
    }

    /** The socket came back after a drop; the resubscribe doesn't re-send candles missed while it was down. */
    private onSocketOpen = () => {
        // First connect (no message seen yet): the initial load is already current.
        if (!this.lastSocketMessageAt || !this.wasTradingSince(this.lastSocketMessageAt)) return
        this.scheduleBackfill(this.getQuietSocketBackfillFrom())
    }

    private onVisibilityChange = () => {
        if (document.hidden) {
            this.hiddenAt = Date.now()
            return
        }
        const hiddenAt = this.hiddenAt
        this.hiddenAt = 0
        // A hidden tab normally keeps receiving ticks; a socket that also went quiet means the page was
        // frozen or the machine slept, and ticks were missed.
        if (hiddenAt && Date.now() - hiddenAt >= BACKFILL_MIN_HIDDEN_MS
            && Date.now() - this.lastSocketMessageAt >= BACKFILL_QUIET_SOCKET_MS
            && this.wasTradingSince(hiddenAt)) {
            this.scheduleBackfill(this.getQuietSocketBackfillFrom())
        }
    }

    /** Page Lifecycle: the browser un-froze this page. */
    private onPageResume = () => {
        if (this.lastSocketMessageAt && !this.wasTradingSince(this.lastSocketMessageAt)) return
        this.scheduleBackfill(this.getQuietSocketBackfillFrom())
    }

    /** Some trading session overlapped [sinceMs, now]: outside market hours a quiet socket missed nothing. */
    private wasTradingSince(sinceMs: number): boolean {
        const calendar = this.chart.calendarData
        if (!calendar) return true
        const from = sinceMs / 1000
        const to = Date.now() / 1000
        for (let day = toDayStart(sinceMs); day.getTime() / 1000 <= to; day = new Date(day.getTime() + 24 * 60 * 60 * 1000)) {
            const dayStart = day.getTime() / 1000
            if (getTradingSessionsForDate(dayStart, calendar).some((s) => dayStart + s.startTime < to && dayStart + s.endTime > from)) {
                return true
            }
        }
        return false
    }

    /** Chart-zone time one bar before the socket's last message (or before the last bar if there was none). */
    private getQuietSocketBackfillFrom(): number {
        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        if (this.lastSocketMessageAt) {
            return convertToTimeZone(this.lastSocketMessageAt / 1000, this.chart.timezone) - timeWidth
        }
        const [lastCandle] = this.getData('primary').slice(-1)
        return lastCandle ? (lastCandle.time as number) - timeWidth : 0
    }

    /** Batch triggers (a reconnect and the first tick after it) into one backfill starting at the earliest time. */
    private scheduleBackfill(fromTime: number, delayMs = 300) {
        if (!fromTime || this.chart.destroyed || this.chart.disableRealtimeUpdates) return
        this.pendingBackfillFrom = Math.min(this.pendingBackfillFrom ?? Infinity, fromTime)
        if (this.backfillTimer !== undefined) return
        this.backfillTimer = window.setTimeout(() => {
            this.backfillTimer = undefined
            const from = this.pendingBackfillFrom
            this.pendingBackfillFrom = undefined
            if (from !== undefined) void this.backfillLiveData(from)
        }, delayMs)
    }

    /**
     * Re-fetch candles after `fromTime` (chart-zone seconds) up to now and take the server's bars in place of
     * what the chart built meanwhile: a jump over the missed bars, or flat fillers from checkForCandleComplete.
     */
    private backfillLiveData = async (fromTime: number) => {
        if (this.chart.destroyed || this.chart.replayMode || this.chart.gotoDate) return
        // Not live (go-to-date / history mode): the forward fetch fills the chart instead.
        if (!this.updateSocketData) return
        if (this.dataLoading) {
            this.scheduleBackfill(fromTime, 2000)
            return
        }
        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        const pending = new Map<string, { security: Security; from: number }>()
        this.chart.getAllSecurities().forEach((security) => {
            const key = getSecurityKey(security.exchange, security.code)
            const [lastCandle] = (this.data[key] || []).slice(-1)
            if (!lastCandle) return
            pending.set(key, { security, from: Math.min(fromTime, (lastCandle.time as number) - timeWidth) })
        })
        if (!pending.size) return

        const fetchGeneration = this.fetchGeneration
        // Parks live ticks in advanceData and holds off other fetches until the merge below.
        this.setDataLoading(true)
        let failed = false
        for (let round = 0; round < BACKFILL_MAX_ROUNDS && pending.size; round++) {
            let response: GetCandlesBulkResponseV2
            try {
                response = await getCandlesBulkV2([...pending.values()].map(({ security, from }) =>
                    getCandleRequestV2(0, BACKFILL_CHUNK, security, this.chart.timeBucket, convertFromUTC(from, this.chart.timezone) * 1000, false, true)
                ))
            } catch (error) {
                console.error('chart: live backfill failed', error)
                failed = true
                break
            }
            // An initial load (new symbol / timeframe) started meanwhile: it owns dataLoading and the data now.
            if (this.chart.destroyed || fetchGeneration !== this.fetchGeneration) return
            const next = new Map<string, { security: Security; from: number }>()
            response.responses.forEach((r) => {
                const lastTime = this.mergeBackfillResponse(r)
                const key = r.security && getSecurityKey(r.security.exchange, r.security.code)
                const entry = key && pending.get(key)
                if (entry && lastTime !== undefined && r.forwardCandles.length >= BACKFILL_CHUNK) {
                    next.set(key, { ...entry, from: lastTime })
                }
            })
            pending.clear()
            next.forEach((v, k) => pending.set(k, v))
        }
        this.mergeAdvanceData()
        this.dataRequestType = DataUpdateRequestType.HISTORICAL
        this.createPrimaryData(true)
        // Full redraw (resets dataLoading): bars were inserted before the last one, a LIVE pass wouldn't show them.
        this.chart.onDataLoaded(true, false, DataUpdateRequestType.HISTORICAL)
        if (failed && ++this.backfillFailures <= BACKFILL_MAX_RETRIES) {
            this.scheduleBackfill(fromTime, 5000 * this.backfillFailures)
        } else if (!failed) {
            this.backfillFailures = 0
        }
    }

    /**
     * Replace this security's bars from the first fetched candle on. After the response, keep only real
     * (tick-built) bars: the no-trade fillers there were copied from an older close, so rebuild them from
     * the latest real candle. An empty response (an illiquid contract that didn't trade) changes nothing.
     */
    private mergeBackfillResponse(response: GetCandlesResponseV2): number | undefined {
        if (!response.security || !response.forwardCandles.length) return
        const key = getSecurityKey(response.security.exchange, response.security.code)
        const security = this.chart.securityMap[key]
        const existing = this.data[key]
        if (!security || !existing?.length) return
        const fetched = prepareSeriesData({ candles: response.forwardCandles, priceMultiplier: security.priceMultiplier || 100, timezone: this.chart.timezone, lastTickTimestamp: response.lastCandleStoreUpdateTime, fillEmptySlots: this.fillEmptySlots, timeBucket: this.chart.timeBucket, calendarData: this.calendarData, security })
        if (!fetched.length) return
        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        const fill = (startCandle: ChartCandle, endCandle: ChartCandle) => fillCandles({ startCandle, endCandle, timeBucket: this.chart.timeBucket, calendarData: this.chart.calendarData, timezone: this.chart.timezone })
        const firstTime = fetched[0].time as number
        const lastTime = fetched[fetched.length - 1].time as number
        const merged = existing.filter((c) => (c.time as number) < firstTime)
        if (merged.length) merged.push(...fill(merged[merged.length - 1], fetched[0]))
        merged.push(...fetched)
        let previous = fetched[fetched.length - 1]
        existing.forEach((c) => {
            if ((c.time as number) <= lastTime || c.synthetic) return
            merged.push(...fill(previous, c), c)
            previous = c
        })
        // Keep the timeline as long as it was (no-trade bars up to the current minute), flat at the latest close.
        const lastExistingTime = existing[existing.length - 1].time as number
        if (lastExistingTime > (previous.time as number)) {
            merged.push(...fill(previous, { ...previous, time: (lastExistingTime + timeWidth) as Time }))
        }
        this.data[key] = merged
        return lastTime
    }

    checkForCandleComplete = () => {
        if (this.chart.destroyed) return
        const timeWidth = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        // While a load or backfill is in flight its response owns the tail; flat fillers pushed now would be wrong.
        if (!this.updateSocketData || this.dataLoading) {
            window.clearTimeout(this.candleCheckTimeout)
            this.candleCheckTimeout = window.setTimeout(this.checkForCandleComplete, 1000);
            return;
        }

        const currentTime = Date.now()
        const tradingSession = getActiveOrNextTradingSession(currentTime / 1000, this.chart.calendarData!);
        const dayStart = toDayStart(currentTime).getTime() / 1000

        if (!tradingSession) {
            // Closed for the day (or a holiday): keep the chain alive so a tab left open fills tomorrow's bars.
            window.clearTimeout(this.candleCheckTimeout)
            this.candleCheckTimeout = window.setTimeout(this.checkForCandleComplete, 5 * 60 * 1000)
            return;
        }

        if (currentTime / 1000 < (dayStart + tradingSession.startTime)) {
            window.clearTimeout(this.candleCheckTimeout)
            this.candleCheckTimeout = window.setTimeout(this.checkForCandleComplete, (dayStart + tradingSession.startTime) * 1000 - currentTime + 1200);
            return;
        }
        const currentCandleTime = getCurrentCandleStartTime(Math.floor(currentTime / 1000) - 0.1, this.chart.chartConfig.timeBucket, this.chart.calendarData!);
        const currentCandleTimeIst = utcToIST(currentCandleTime)
        let update = false;
        this.chart.getAllSecurities().forEach((sec) => {
            const key = getSecurityKey(sec.exchange, sec.code);
            const data = this.data[key];

            if (!data || !data.length) return;
            const lastCandle = data[data.length - 1];
            if (currentCandleTimeIst > (lastCandle.time as number) + timeWidth) {
                // Every completed session slot without a tick gets a flat bar, not just the latest one: a
                // throttled (background) or late timer can find several. Lost ticks are the backfill's job.
                const fillers = fillCandles({ startCandle: lastCandle, endCandle: { ...lastCandle, time: currentCandleTimeIst as Time }, timeBucket: this.chart.timeBucket, calendarData: this.chart.calendarData, timezone: this.chart.timezone })
                if (fillers.length) {
                    data.push(...fillers);
                    update = true;
                }
            }
        });

        if (update) {
            this.dataRequestType = DataUpdateRequestType.LIVE
            this.createPrimaryData(true)
            this.chart.onDataLoaded(true, false, DataUpdateRequestType.LIVE);
        }

        const primaryData = this.chart.getRawData();

        if (primaryData.length) {
            const lastCandle = primaryData[primaryData.length - 1];
            const candleEndTime = (lastCandle.time as number) + 2 * timeWidth;

            window.clearTimeout(this.candleCheckTimeout)
            this.candleCheckTimeout = window.setTimeout(this.checkForCandleComplete, istToUTC(candleEndTime) * 1000 - Date.now() + 1200);
        }
    }
    pushReplayData(data: Record<string, ChartCandle>) {
        const dataKeys = Object.keys(data)
        if (!dataKeys.length) return;
        const securities = this.chart.getAllSecurities();
        const timeWidth: number = getTimeWidthInSeconds(this.chart.chartConfig.timeBucket)
        const newCandleTime = data[dataKeys[0]].time as number
        securities.forEach((sec) => {
            const key = getSecurityKey(sec.exchange, sec.code)
            const newCandle = data[key];
            const existingData = this.data[key]
            const lastCandle = existingData[existingData.length - 1];
            if (!lastCandle) return;
            const currentCandleTime = lastCandle.time as number;
            const currentCandleEndTime = currentCandleTime + timeWidth;
            if (data[key]) {
                if (newCandleTime < currentCandleEndTime) {
                    existingData[existingData.length - 1] = mergeCandles(lastCandle, newCandle)
                } else {
                    existingData.push(newCandle)
                }
            } else if (newCandleTime >= currentCandleEndTime) {
                existingData.push({
                    ...getInitialCandle(),
                    time: currentCandleEndTime as Time,
                    close: lastCandle.close,
                    open: lastCandle.close,
                    high: lastCandle.close,
                    low: lastCandle.close
                })
            }
        })

        this.dataRequestType = DataUpdateRequestType.LIVE
        this.createPrimaryData(true)
        this.chart.onDataLoaded(true)
    }

    getData(key: string) {
        return this.data[key] || []
    }

    isPrevOutOfData(key: string) {
        return this.prevOutOfData[key] || false
    }

    isNextOutofData(key: string) {
        return this.nextOutOfData[key] || false
    }

    resetDataFetchType() {
        this.dataRequestType = undefined
    }

    destroy() {
        this.chart.ws.removeListener(this.onWebSocketMessageReceived)
        this.chart.ws.removeOpenListener(this.onSocketOpen)
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
        document.removeEventListener('resume', this.onPageResume)
        clearTimeout(this.candleCheckTimeout)
        clearTimeout(this.backfillTimer)
    }
}

export default ChartDataManager